import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Chip,
    Divider,
    TextField,
    CircularProgress,
    Alert,
    Paper,
    Tabs,
    Tab,
    alpha
} from '@mui/material';
import {
    X as CloseIcon,
    Send as SendIcon,
    Users as UsersIcon,
    FileText as FileTextIcon,
    ExternalLink as OpenIcon,
    ChevronDown as ChevronDownIcon,
    ChevronUp as ChevronUpIcon,
    Bot as BotIcon,
    User as UserIcon,
    Edit as EditIcon,
    Check as CheckIcon,
    X as CancelIcon
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { coursesApi } from '../../services/api';
import RichTextEditor from '../common/RichTextEditor';

const AssignmentDetailDialog = ({ open, onClose, assignment, token, mode = 'student' }) => {
    const { t } = useTranslation();
    const [tabValue, setTabValue] = useState(0);
    const [solution, setSolution] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSendingFeedback, setIsSendingFeedback] = useState(false);
    const [error, setError] = useState(null);
    const [submissions, setSubmissions] = useState([]);
    const [loadingSubmissions, setLoadingSubmissions] = useState(false);
    const [studentSubmissions, setStudentSubmissions] = useState([]);
    const [expandedStudents, setExpandedStudents] = useState({});
    const [feedbackStates, setFeedbackStates] = useState({}); // { submissionId: { text: '', isEditing: false } }

    const loadSubmissions = useCallback(async () => {
        if (!assignment || !token) return;

        setLoadingSubmissions(true);
        try {
            const data = await coursesApi.getAssignmentSubmissions(assignment.id, token);

            if (mode === 'student') {
                // For students, show only their submissions
                setSubmissions(data || []);
            } else {
                // For tutors, group submissions by student
                const grouped = {};
                (data || []).forEach(sub => {
                    const userId = sub.user?.id || sub.user_id;
                    if (!grouped[userId]) {
                        grouped[userId] = [];
                    }
                    grouped[userId].push(sub);
                });
                const studentSubs = Object.entries(grouped).map(([userId, subs]) => ({
                    userId,
                    username: subs[0].user?.full_name || subs[0].user?.username || `User ${userId}`,
                    submissions: subs.sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at))
                }));
                setStudentSubmissions(studentSubs);
                // Auto-expand first student
                if (studentSubs.length > 0) {
                    setExpandedStudents({ [studentSubs[0].userId]: true });
                }
            }
        } catch (err) {
            setError(t('assignment_detail.errors.load_submissions_failed'));
        } finally {
            setLoadingSubmissions(false);
        }
    }, [assignment, token, mode, t]);

    useEffect(() => {
        if (open) {
            loadSubmissions();
        }
    }, [open, loadSubmissions]);

    const handleSubmitSolution = async () => {
        if (!solution.trim()) {
            setError(t('assignment_detail.errors.empty_solution'));
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            await coursesApi.createSubmission(assignment.id, { content: solution }, token);
            await loadSubmissions();
            setSolution('');
            setTabValue(1); // Switch to submissions tab
        } catch (err) {
            setError(err.message || t('assignment_detail.errors.submission_failed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSendFeedback = async (submissionId, feedbackText) => {
        if (!feedbackText?.trim()) {
            setError(t('assignment_detail.errors.empty_feedback'));
            return;
        }

        setIsSendingFeedback(true);
        setError(null);

        try {
            await coursesApi.updateSubmissionFeedback(submissionId, { feedback: feedbackText }, token);
            await loadSubmissions();
            // Clear feedback state for this submission
            setFeedbackStates(prev => ({
                ...prev,
                [submissionId]: { text: '', isEditing: false }
            }));
        } catch (err) {
            setError(err.message || t('assignment_detail.errors.feedback_failed'));
        } finally {
            setIsSendingFeedback(false);
        }
    };

    const toggleStudent = (userId) => {
        setExpandedStudents(prev => ({
            ...prev,
            [userId]: !prev[userId]
        }));
    };

    const startEditingFeedback = (submissionId, currentFeedback) => {
        setFeedbackStates(prev => ({
            ...prev,
            [submissionId]: { text: currentFeedback, isEditing: true }
        }));
    };

    const cancelEditingFeedback = (submissionId) => {
        setFeedbackStates(prev => ({
            ...prev,
            [submissionId]: { text: '', isEditing: false }
        }));
    };

    const renderStudentView = () => (
        <Box>
            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tab
                    label={t('assignment_detail.tabs.description')}
                    icon={<FileTextIcon size={16} />}
                    iconPosition="start"
                    sx={{ '& .lucide': { color: '#2196F3' } }}
                />
                <Tab
                    label={t('assignment_detail.tabs.submit')}
                    icon={<SendIcon size={16} />}
                    iconPosition="start"
                    sx={{ '& .lucide': { color: '#4CAF50' } }}
                />
            </Tabs>

            {/* Description Tab */}
            {tabValue === 0 && (
                <Box sx={{ py: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        <ReactMarkdown>{assignment.description}</ReactMarkdown>
                    </Typography>

                    {assignment.correct_answer && (
                        <Paper sx={{ p: 2, bgcolor: alpha('#2196F3', 0.05), borderRadius: 1, mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                                {t('assignment_detail.acceptance_criteria')}
                            </Typography>
                            <Typography variant="body2">{assignment.correct_answer}</Typography>
                        </Paper>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, mt: 3, flexWrap: 'wrap' }}>
                        {assignment.points > 0 && (
                            <Chip label={`${assignment.points} ${t('assignment_detail.points')}`} color="warning" size="small" />
                        )}
                    </Box>

                    {/* Button to open sandbox for sandbox type assignments */}
                    {assignment.assignment_type === 'sandbox' && assignment.shared_sandbox && (
                        <Box sx={{ mt: 3 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<OpenIcon size={18} />}
                                onClick={() => {
                                    const url = `/sandbox/${assignment.shared_sandbox}?assignment_id=${assignment.id}&mode=assignment`;
                                    window.open(url, '_blank');
                                }}
                            >
                                {t('assignment_detail.open_sandbox')}
                            </Button>
                        </Box>
                    )}
                </Box>
            )}

            {/* Submit Tab */}
            {tabValue === 1 && (
                <Box sx={{ py: 3 }}>
                    {/* History of submissions and feedbacks */}
                    {submissions.length > 0 && (
                        <Box sx={{ mb: 3, maxHeight: '400px', overflowY: 'auto', pr: 1 }}>
                            {submissions.slice().reverse().map((submission) => (
                                <Box key={submission.id} sx={{ mb: 2 }}>
                                    {/* Student's answer (right side, blue) */}
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                        <Box
                                            sx={{
                                                maxWidth: '70%',
                                                bgcolor: alpha('#2196F3', 0.1),
                                                borderRadius: 2,
                                                p: 2,
                                                position: 'relative'
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    '& p': { margin: 0, marginBottom: 1 },
                                                    '& p:last-child': { marginBottom: 0 },
                                                    '& ul, & ol': { marginTop: 0.5, marginBottom: 0.5, paddingLeft: 3 },
                                                    '& li': { marginBottom: 0.5 },
                                                    '& code': {
                                                        bgcolor: alpha('#000', 0.05),
                                                        padding: '2px 6px',
                                                        borderRadius: 0.5,
                                                        fontFamily: 'monospace',
                                                        fontSize: '0.9em'
                                                    },
                                                    '& pre': {
                                                        bgcolor: alpha('#000', 0.05),
                                                        padding: 2,
                                                        borderRadius: 1,
                                                        overflow: 'auto',
                                                        marginTop: 1,
                                                        marginBottom: 1
                                                    },
                                                    '& pre code': {
                                                        bgcolor: 'transparent',
                                                        padding: 0
                                                    }
                                                }}
                                                dangerouslySetInnerHTML={{ __html: submission.content }}
                                            />
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'right' }}>
                                                {new Date(submission.submitted_at).toLocaleString()}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Feedback (left side, green) */}
                                    {submission.feedback && (
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
                                            <Box
                                                sx={{
                                                    maxWidth: '70%',
                                                    bgcolor: alpha('#4CAF50', 0.1),
                                                    borderRadius: 2,
                                                    p: 2,
                                                    position: 'relative'
                                                }}
                                            >
                                                <Box sx={{
                                                    '& p': { margin: 0, marginBottom: 1 },
                                                    '& p:last-child': { marginBottom: 0 }
                                                }}>
                                                    <ReactMarkdown>{submission.feedback}</ReactMarkdown>
                                                </Box>
                                                <Box sx={{ mt: 1 }}>
                                                    {submission.feedback_at && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                            {new Date(submission.feedback_at).toLocaleString()}
                                                        </Typography>
                                                    )}
                                                    {submission.feedback_edited_at && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                                                            {t('assignment_detail.feedback_edited', {
                                                                date: new Date(submission.feedback_edited_at).toLocaleString()
                                                            })}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* Check if max attempts reached */}
                    {assignment.max_attempts && submissions.length >= assignment.max_attempts ? (
                        <Alert severity="warning">
                            {t('assignment_detail.max_attempts_reached')}
                        </Alert>
                    ) : (
                        <Box>
                            <RichTextEditor
                                value={solution}
                                onChange={setSolution}
                                placeholder={t('assignment_detail.solution_placeholder')}
                                minHeight="450px"
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    {t('assignment_detail.submissions_count', { count: submissions.length })}
                                    {assignment.max_attempts && ` / ${assignment.max_attempts}`}
                                </Typography>
                                <Button
                                    variant="contained"
                                    onClick={handleSubmitSolution}
                                    disabled={isSubmitting || !solution.trim()}
                                    startIcon={isSubmitting ? <CircularProgress size={20} /> : <SendIcon size={20} />}
                                >
                                    {isSubmitting ? t('assignment_detail.submitting') : t('assignment_detail.submit')}
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Box>
            )}

        </Box>
    );

    const renderTutorView = () => (
        <Box>
            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tab label={t('assignment_detail.tabs.description')} icon={<FileTextIcon size={16} />} iconPosition="start" />
                <Tab label={t('assignment_detail.tabs.student_submissions')} icon={<UsersIcon size={16} />} iconPosition="start" />
            </Tabs>

            {/* Description Tab */}
            {tabValue === 0 && (
                <Box sx={{ py: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        <ReactMarkdown>{assignment.description}</ReactMarkdown>
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 3, flexWrap: 'wrap' }}>
                        <Chip label={t(`assignment_detail.type.${assignment.assignment_type}`)} color="primary" size="small" />
                        {assignment.points > 0 && (
                            <Chip label={`${assignment.points} ${t('assignment_detail.points')}`} color="warning" size="small" />
                        )}
                    </Box>
                </Box>
            )}

            {/* Student Submissions Tab */}
            {tabValue === 1 && (
                <Box sx={{ py: 3 }}>
                    {loadingSubmissions ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : studentSubmissions.length === 0 ? (
                        <Alert severity="info">{t('assignment_detail.no_student_submissions')}</Alert>
                    ) : (
                        <Box>
                            {studentSubmissions.map(({ userId, username, submissions: subs }) => {
                                const isExpanded = expandedStudents[userId];
                                const latestSubmission = subs[0];

                                return (
                                    <Paper
                                        key={userId}
                                        sx={{
                                            mb: 2,
                                            overflow: 'hidden',
                                            border: '1px solid',
                                            borderColor: 'divider'
                                        }}
                                    >
                                        {/* Student Header - Always visible */}
                                        <Box
                                            sx={{
                                                p: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                cursor: 'pointer',
                                                bgcolor: alpha('#2196F3', 0.05),
                                                '&:hover': {
                                                    bgcolor: alpha('#2196F3', 0.1)
                                                }
                                            }}
                                            onClick={() => toggleStudent(userId)}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Typography variant="h6">
                                                    {username}
                                                </Typography>
                                                <Chip
                                                    label={`${subs.length} ${t('assignment_detail.submissions_label')}`}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                                {latestSubmission.grade && (
                                                    <Chip
                                                        label={`${t('assignment_detail.latest_grade')}: ${latestSubmission.grade}`}
                                                        size="small"
                                                        color="success"
                                                    />
                                                )}
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {t('assignment_detail.latest')}: {new Date(latestSubmission.submitted_at).toLocaleDateString()}
                                                </Typography>
                                                {isExpanded ? <ChevronUpIcon size={20} /> : <ChevronDownIcon size={20} />}
                                            </Box>
                                        </Box>

                                        {/* Student Submissions - Collapsible */}
                                        {isExpanded && (
                                            <Box sx={{ p: 2 }}>
                                                {subs.map((sub, idx) => {
                                                    const feedbackState = feedbackStates[sub.id] || { text: '', isEditing: false };
                                                    const isAIFeedback = sub.feedback && !sub.reviewed_by;

                                                    return (
                                                        <Box key={sub.id} sx={{ mb: idx < subs.length - 1 ? 3 : 0 }}>
                                                            {/* Submission Header */}
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                                <Typography variant="subtitle2" color="text.secondary">
                                                                    {t('assignment_detail.submission')} #{subs.length - idx}
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                                    <Chip
                                                                        label={new Date(sub.submitted_at).toLocaleString()}
                                                                        size="small"
                                                                        variant="outlined"
                                                                    />
                                                                    {sub.grade && (
                                                                        <Chip
                                                                            label={`${t('assignment_detail.grade')}: ${sub.grade}`}
                                                                            size="small"
                                                                            color="warning"
                                                                        />
                                                                    )}
                                                                </Box>
                                                            </Box>

                                                            {/* Student Answer */}
                                                            <Box sx={{ mb: 2 }}>
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                                                                    {t('assignment_detail.student_answer')}:
                                                                </Typography>
                                                                <Box
                                                                    sx={{
                                                                        bgcolor: alpha('#2196F3', 0.08),
                                                                        borderRadius: 2,
                                                                        p: 2,
                                                                        border: '1px solid',
                                                                        borderColor: alpha('#2196F3', 0.2)
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            '& p': { margin: 0, marginBottom: 1 },
                                                                            '& p:last-child': { marginBottom: 0 },
                                                                            '& ul, & ol': { marginTop: 0.5, marginBottom: 0.5, paddingLeft: 3 },
                                                                            '& li': { marginBottom: 0.5 },
                                                                            '& code': {
                                                                                bgcolor: alpha('#000', 0.05),
                                                                                padding: '2px 6px',
                                                                                borderRadius: 0.5,
                                                                                fontFamily: 'monospace',
                                                                                fontSize: '0.9em'
                                                                            },
                                                                            '& pre': {
                                                                                bgcolor: alpha('#000', 0.05),
                                                                                padding: 2,
                                                                                borderRadius: 1,
                                                                                overflow: 'auto',
                                                                                marginTop: 1,
                                                                                marginBottom: 1
                                                                            },
                                                                            '& pre code': {
                                                                                bgcolor: 'transparent',
                                                                                padding: 0
                                                                            }
                                                                        }}
                                                                        dangerouslySetInnerHTML={{ __html: sub.content }}
                                                                    />
                                                                </Box>
                                                            </Box>

                                                            {/* Feedback Section */}
                                                            {sub.feedback && !feedbackState.isEditing ? (
                                                                <Box sx={{ mb: 2 }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                                            {t('assignment_detail.feedback')}:
                                                                        </Typography>
                                                                        {isAIFeedback ? (
                                                                            <Chip
                                                                                icon={<BotIcon size={14} />}
                                                                                label={t('assignment_detail.ai_feedback')}
                                                                                size="small"
                                                                                color="info"
                                                                                variant="outlined"
                                                                            />
                                                                        ) : (
                                                                            <Chip
                                                                                icon={<UserIcon size={14} />}
                                                                                label={`${t('assignment_detail.feedback_from')}: ${sub.reviewed_by?.full_name || sub.reviewed_by?.username || t('assignment_detail.tutor')}`}
                                                                                size="small"
                                                                                color="primary"
                                                                                variant="outlined"
                                                                            />
                                                                        )}
                                                                    </Box>
                                                                    <Box
                                                                        sx={{
                                                                            bgcolor: alpha('#4CAF50', 0.08),
                                                                            borderRadius: 2,
                                                                            p: 2,
                                                                            border: '1px solid',
                                                                            borderColor: alpha('#4CAF50', 0.2)
                                                                        }}
                                                                    >
                                                                        <Box sx={{
                                                                            '& p': { margin: 0, marginBottom: 1 },
                                                                            '& p:last-child': { marginBottom: 0 }
                                                                        }}>
                                                                            <ReactMarkdown>{sub.feedback}</ReactMarkdown>
                                                                        </Box>
                                                                        <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                            <Box>
                                                                                {sub.feedback_at && (
                                                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                                        {new Date(sub.feedback_at).toLocaleString()}
                                                                                    </Typography>
                                                                                )}
                                                                                {sub.feedback_edited_at && (
                                                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                                                                                        {t('assignment_detail.edited_by', {
                                                                                            name: sub.reviewed_by?.full_name || sub.reviewed_by?.username || t('assignment_detail.tutor'),
                                                                                            date: new Date(sub.feedback_edited_at).toLocaleString()
                                                                                        })}
                                                                                    </Typography>
                                                                                )}
                                                                            </Box>
                                                                            <Button
                                                                                size="small"
                                                                                startIcon={<EditIcon size={14} />}
                                                                                onClick={() => startEditingFeedback(sub.id, sub.feedback)}
                                                                            >
                                                                                {t('assignment_detail.edit')}
                                                                            </Button>
                                                                        </Box>
                                                                    </Box>
                                                                </Box>
                                                            ) : (
                                                                <Box sx={{ mb: 2 }}>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                                                                        {sub.feedback ? t('assignment_detail.edit_feedback') : t('assignment_detail.provide_feedback')}:
                                                                    </Typography>
                                                                    <TextField
                                                                        fullWidth
                                                                        multiline
                                                                        rows={4}
                                                                        value={feedbackState.text}
                                                                        onChange={(e) => setFeedbackStates(prev => ({
                                                                            ...prev,
                                                                            [sub.id]: { ...feedbackState, text: e.target.value }
                                                                        }))}
                                                                        placeholder={t('assignment_detail.feedback_placeholder')}
                                                                        sx={{ mb: 1 }}
                                                                    />
                                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                                        <Button
                                                                            variant="contained"
                                                                            size="small"
                                                                            startIcon={isSendingFeedback ? <CircularProgress size={14} /> : <CheckIcon size={14} />}
                                                                            onClick={() => handleSendFeedback(sub.id, feedbackState.text)}
                                                                            disabled={isSendingFeedback || !feedbackState.text?.trim()}
                                                                        >
                                                                            {t('assignment_detail.save')}
                                                                        </Button>
                                                                        {feedbackState.isEditing && (
                                                                            <Button
                                                                                variant="outlined"
                                                                                size="small"
                                                                                startIcon={<CancelIcon size={14} />}
                                                                                onClick={() => cancelEditingFeedback(sub.id)}
                                                                                disabled={isSendingFeedback}
                                                                            >
                                                                                {t('assignment_detail.cancel')}
                                                                            </Button>
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            )}

                                                            {idx < subs.length - 1 && <Divider sx={{ my: 2 }} />}
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        )}
                                    </Paper>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );

    if (!assignment) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {assignment.title}
                </Typography>
                <Button
                    onClick={onClose}
                    size="small"
                    sx={{ minWidth: 'auto', p: 1 }}
                >
                    <CloseIcon size={20} />
                </Button>
            </DialogTitle>

            <DialogContent dividers>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {mode === 'student' ? renderStudentView() : renderTutorView()}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} variant="outlined">
                    {t('assignment_detail.close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AssignmentDetailDialog;
